/**
 * errMsg — mensagem amigável (pt-BR) a partir de um erro de mutation/fetch.
 *
 * Fonte única reusada pelas telas (antes cada uma tinha sua cópia inline). Sabe
 * desempacotar o `ApiError` do cliente HTTP: o backend responde `{ error, message }`
 * e o `ApiError` guarda o CÓDIGO em `.message` e o corpo inteiro em `.response` —
 * então a mensagem amigável de verdade está em `response.message`.
 *
 * Retorna `null` quando não há erro (deixa o chamador esconder o Alert/toast).
 */
import { ApiError } from './api';

/** FX-3: rótulos pt-BR p/ nomear o campo em erros de validação (Zod). */
const FIELD_LABELS: Record<string, string> = {
  nome: 'Nome',
  cnpj: 'CNPJ',
  email: 'E-mail',
  telefone: 'Telefone',
  observacoes: 'Observações',
  apelido: 'Apelido',
  impostoPct: '% de imposto',
  aliquotaNfPct: 'Alíquota de NF',
  minFaturarVendaCentavos: 'Mínimo (venda)',
  minFaturarManutencaoCentavos: 'Mínimo (manutenção)',
  cotacaoUnitCentavos: 'Cotação unitária',
  descontoPct: '% de desconto',
  quantidade: 'Quantidade',
  vendaTotalCentavos: 'Venda total',
  titulo: 'Título',
  item: 'Item',
  situacao: 'Situação',
  // C-01: campos da ficha do cliente. Sem eles, um 400 de validação aparecia
  // como o nome cru do campo — foi o que fez o erro do CNAE demorar a ser
  // diagnosticado.
  cnae: 'CNAE',
  razaoSocial: 'Razão social',
  nomeFantasia: 'Nome fantasia',
  enderecoLogradouro: 'Logradouro',
  enderecoNumero: 'Número',
  enderecoBairro: 'Bairro',
  enderecoCidade: 'Cidade',
  enderecoUf: 'UF',
  enderecoCep: 'CEP',
  ie: 'Inscrição estadual',
  im: 'Inscrição municipal',
};

function labelField(path: string): string {
  return FIELD_LABELS[path] ?? path;
}

/**
 * FX-3: extrai uma mensagem que NOMEIA o campo a partir de um corpo de erro de
 * validação do Zod. Cobre as duas formas que o backend devolve:
 *   - `{ issues: ZodIssue[] }` (route schemas `.safeParse` → `error.issues`)
 *   - `{ details: { fieldErrors, formErrors } }` (`error.flatten()`)
 * Não prefixa quando a mensagem já nomeia o campo (ex.: "CNPJ deve ter…").
 */
function zodFieldMessage(body: unknown): string | null {
  const b = body as
    | {
        issues?: Array<{ path?: unknown[]; message?: unknown }>;
        details?: { fieldErrors?: Record<string, unknown>; formErrors?: unknown[] };
      }
    | null
    | undefined;
  if (!b) return null;

  const withLabel = (path: string, msg: string): string => {
    const label = labelField(path);
    if (label && !msg.toLowerCase().includes(label.toLowerCase())) return `${label}: ${msg}`;
    return msg;
  };

  if (Array.isArray(b.issues) && b.issues.length > 0) {
    const i = b.issues[0];
    const msg = typeof i.message === 'string' ? i.message : 'valor inválido';
    const path = Array.isArray(i.path)
      ? i.path.filter((p) => typeof p === 'string' || typeof p === 'number').join('.')
      : '';
    return path ? withLabel(path, msg) : msg;
  }

  const fe = b.details?.fieldErrors;
  if (fe && typeof fe === 'object') {
    for (const [field, msgs] of Object.entries(fe)) {
      if (Array.isArray(msgs) && msgs.length > 0 && typeof msgs[0] === 'string') return withLabel(field, msgs[0]);
    }
  }
  const formErrors = b.details?.formErrors;
  if (Array.isArray(formErrors) && typeof formErrors[0] === 'string') return formErrors[0];
  return null;
}

// A-3 (auditoria 13/08): códigos que o backend devolve sem `message` e que a
// tela pode receber em fluxo normal. Cada um ganha a SUA frase; o que não
// estiver aqui cai no fallback — nunca no código cru.
const CODIGO_TRADUZIDO: Record<string, string> = {
  'feature-disabled': 'Esse recurso está desativado.',
};

/**
 * A-3: kebab/snake multi-segmento ("feature-disabled", "invalid_body") é
 * vocabulário de máquina — não é frase pra pessoa. Uma palavra solta sem
 * separador ("Aguarde") passa; é o separador que denuncia o código.
 */
function pareceCodigo(s: string): boolean {
  return /^[a-z0-9]+([-_][a-z0-9]+)+$/.test(s.trim());
}

export function errMsg(
  error: unknown,
  fallback = 'Algo deu errado. Tente de novo.',
): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    // `mensagem` incluída (A-3): as rotas do CRM respondem `{ erro, mensagem }`
    // — o vocabulário PT da casa — e só `message` era lido.
    const body = error.response as
      | { message?: string; mensagem?: string; error?: string; erro?: string }
      | null
      | undefined;
    const humana = body?.message ?? body?.mensagem;
    if (humana && !pareceCodigo(humana)) return humana;
    // FX-3: "invalid-body" cru → mostra QUAL campo está errado (Zod issues).
    const zod = zodFieldMessage(error.response);
    if (zod) return zod;
    const codigo = body?.error ?? body?.erro ?? error.message;
    if (codigo && CODIGO_TRADUZIDO[codigo]) return CODIGO_TRADUZIDO[codigo];
    // A-3: antes devolvia `error.message` — que no ApiError É o código.
    if (error.message && !pareceCodigo(error.message)) return error.message;
    return fallback;
  }
  const e = error as { data?: { message?: string }; message?: string };
  const msg = e.data?.message ?? e.message;
  if (msg && !pareceCodigo(msg)) return msg;
  return fallback;
}
